/**
 * Per-locale plural rules for vue-i18n (CG20-6218).
 *
 * vue-i18n applies its default English rule to every locale unless a rule is
 * registered here: branch 0 when n is 0, branch 1 when n is 1, branch 2 for
 * every n >= 2. Czech, Polish, Ukrainian, Romanian and Arabic need more
 * branches than that, so they pick the wrong one on every pluralized key.
 *
 * Branch convention. Branch 0 is the literal-zero form ("0 users"), a
 * convention of this project rather than a CLDR category; the branches after
 * it follow CLDR:
 *   cs, pl, uk   0 | one | few | many
 *   ro           0 | one | few (n % 100 in 1..19) | other (20+, takes "de")
 *   ar           0 | one | two | few (3-10) | many (11-99) | other (100+)
 *
 * The clamp is the important part, not a detail. vue-i18n passes the number of
 * branches the message actually has, so clamping the index to it makes a
 * message that has not been given its extra branches yet behave exactly as it
 * does today, instead of resolving out of range. Without the clamp the blast
 * radius would not be the pluralized keys but every message called with a
 * positional count, including single-branch ones such as General.Points
 * ('{count} Points'), which has nothing to do with plurals. The clamp is what
 * makes the rollout incremental per key instead of atomic per locale.
 *
 * Adding branches in the locale files is therefore safe in any order, and a
 * missing branch is reported in development by the warning below.
 *
 * Known limitation. Ukrainian numbers ending in 1 but not 11 (21, 31, 101)
 * belong to the CLDR "one" category, and this rule deliberately does not send
 * them there: branch 1 spells out "1", so they would render the wrong number.
 * They fall to the few/many branch instead, which is the current behaviour. To
 * fix them properly, rewrite branch 1 of the Ukrainian plural keys with
 * {count} in place of the literal 1, then drop the floor for uk.
 */

const CLDR_INDEX = {
  cs: (n) => (n === 1 ? 1 : (n >= 2 && n <= 4 ? 2 : 3)),

  pl: (n) => {
    if (n === 1) return 1;
    const d10 = n % 10;
    const d100 = n % 100;
    if (d10 >= 2 && d10 <= 4 && !(d100 >= 12 && d100 <= 14)) return 2;
    return 3;
  },

  uk: (n) => {
    const d10 = n % 10;
    const d100 = n % 100;
    if (d10 === 1 && d100 !== 11) return 1;
    if (d10 >= 2 && d10 <= 4 && !(d100 >= 12 && d100 <= 14)) return 2;
    return 3;
  },

  ro: (n) => {
    if (n === 1) return 1;
    const d100 = n % 100;
    if (d100 >= 1 && d100 <= 19) return 2;
    return 3;
  },

  ar: (n) => {
    if (n === 1) return 1;
    if (n === 2) return 2;
    const d100 = n % 100;
    if (d100 >= 3 && d100 <= 10) return 3;
    if (d100 >= 11 && d100 <= 99) return 4;
    return 5;
  },
};

function ruleFor(locale) {
  return (choice, choicesLength) => {
    const n = Math.abs(choice);

    // A two-branch message is [singular, plural], not [zero, one, ...]: the
    // literal-zero convention below only applies from three branches up. Without
    // this case the rules below reserve branch 0 for n = 0 and send n = 1 to
    // branch 1, which inverts the two forms — a singular message rendering its
    // plural. Matches vue-i18n's own default rule for two branches.
    if (choicesLength === 2) {
      return n === 1 ? 0 : 1;
    }

    // Branches 0 and 1 are reserved for exactly n = 0 and n = 1, because by
    // this project's convention they spell the number out ("0 users",
    // "1 user"). A CLDR category may legitimately cover other numbers -- the
    // Ukrainian "one" category also holds 21, 31, 101 -- and selecting the
    // literal-one branch for those would print "1 user" for 21 users. The
    // floor keeps the rule off those two branches for every n > 1.
    const wanted = n === 0 ? 0
      : n === 1 ? 1
        : Math.max(2, CLDR_INDEX[locale](n));
    const used = Math.min(wanted, choicesLength - 1);

    if (process.env.NODE_ENV !== 'production' && used !== wanted) {
      // vue-i18n does not pass the key to the rule, so the warning can only
      // describe the shape of the message. console.trace locates the call.
      console.warn(
        `[i18n] ${locale}: message has ${choicesLength} branches, the rule asked for `
        + `index ${wanted} (n=${n}). Used branch ${used} instead — a branch is `
        + `missing in ${locale}.json.`
      );
      console.trace();
    }

    return used;
  };
}

// The other locales keep the default rule, which is correct for all of them.
export default Object.keys(CLDR_INDEX).reduce((rules, locale) => {
  rules[locale] = ruleFor(locale);
  return rules;
}, {});
